import type { GolferArchetype, GolferPersonality, TourGolfer } from './types';

interface TourGolferIdentity {
  id: string;
  firstName: string;
  surname: string;
  region: string;
  age: number;
  archetype: GolferArchetype;
  personality: GolferPersonality;
  handedness: 'left' | 'right';
}

/**
 * Permanent tour identities. Add future players here (or to a future reserve roster), never by
 * deriving an id from their name. Array order intentionally retains the original ranking/id map.
 */
const ACTIVE_TOUR_IDENTITIES: readonly TourGolferIdentity[] = [
  {
    id: 'alex-carter',
    firstName: 'Alex',
    surname: 'Carter',
    region: 'United States',
    age: 27,
    archetype: 'balanced',
    personality: 'confident',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-2',
    firstName: 'Daisuke',
    surname: 'Moriya',
    region: 'Japan',
    age: 31,
    archetype: 'precision',
    personality: 'methodical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-3',
    firstName: 'Callum',
    surname: 'Strachan',
    region: 'Scotland',
    age: 35,
    archetype: 'wind-specialist',
    personality: 'resilient',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-4',
    firstName: 'Thiago',
    surname: 'Varela',
    region: 'Argentina',
    age: 24,
    archetype: 'rising-prospect',
    personality: 'charismatic',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-5',
    firstName: 'Marcus',
    surname: 'Holloway',
    region: 'United States',
    age: 33,
    archetype: 'power-player',
    personality: 'intense',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-6',
    firstName: 'Soren',
    surname: 'Nygaard',
    region: 'Denmark',
    age: 29,
    archetype: 'course-manager',
    personality: 'analytical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-7',
    firstName: 'Min-jae',
    surname: 'Kwon',
    region: 'South Korea',
    age: 26,
    archetype: 'elite-putter',
    personality: 'calm',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-8',
    firstName: 'Elliot',
    surname: 'Mercer',
    region: 'England',
    age: 38,
    archetype: 'veteran',
    personality: 'reserved',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-9',
    firstName: 'Rafael',
    surname: 'Monteiro',
    region: 'Brazil',
    age: 30,
    archetype: 'short-game-specialist',
    personality: 'confident',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-10',
    firstName: 'Nikhil',
    surname: 'Bhasin',
    region: 'India',
    age: 28,
    archetype: 'precision',
    personality: 'analytical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-11',
    firstName: 'Lachlan',
    surname: 'Pritchard',
    region: 'Australia',
    age: 25,
    archetype: 'volatile-aggressor',
    personality: 'aggressive',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-12',
    firstName: 'Emil',
    surname: 'Vesterlund',
    region: 'Sweden',
    age: 34,
    archetype: 'major-specialist',
    personality: 'intense',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-13',
    firstName: 'Gabriel',
    surname: 'Dumont',
    region: 'France',
    age: 23,
    archetype: 'rising-prospect',
    personality: 'streaky',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-14',
    firstName: 'Wesley',
    surname: 'Rourke',
    region: 'United States',
    age: 41,
    archetype: 'veteran',
    personality: 'resilient',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-15',
    firstName: 'Tawanda',
    surname: 'Mbeki',
    region: 'South Africa',
    age: 32,
    archetype: 'recovery-specialist',
    personality: 'calm',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-16',
    firstName: 'Jasper',
    surname: 'Van Aken',
    region: 'Netherlands',
    age: 27,
    archetype: 'balanced',
    personality: 'confident',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-17',
    firstName: 'Connor',
    surname: 'Kelleher',
    region: 'Ireland',
    age: 29,
    archetype: 'short-game-specialist',
    personality: 'charismatic',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-18',
    firstName: 'Malcolm',
    surname: 'Reeves',
    region: 'Canada',
    age: 36,
    archetype: 'course-manager',
    personality: 'methodical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-19',
    firstName: 'Preecha',
    surname: 'Suwannarat',
    region: 'Thailand',
    age: 28,
    archetype: 'elite-putter',
    personality: 'reserved',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-20',
    firstName: 'Jonas',
    surname: 'Falkenberg',
    region: 'Germany',
    age: 31,
    archetype: 'precision',
    personality: 'analytical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-21',
    firstName: 'Mateo',
    surname: 'Quintero',
    region: 'Colombia',
    age: 26,
    archetype: 'power-player',
    personality: 'aggressive',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-22',
    firstName: 'Harun',
    surname: 'Al-Masri',
    region: 'Jordan',
    age: 33,
    archetype: 'recovery-specialist',
    personality: 'resilient',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-23',
    firstName: 'Nathaniel',
    surname: 'Brooks',
    region: 'United States',
    age: 22,
    archetype: 'rising-prospect',
    personality: 'confident',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-24',
    firstName: 'Rhys',
    surname: 'Cadogan',
    region: 'Wales',
    age: 37,
    archetype: 'wind-specialist',
    personality: 'calm',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-25',
    firstName: 'Armand',
    surname: 'Leclercq',
    region: 'Belgium',
    age: 30,
    archetype: 'major-specialist',
    personality: 'intense',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-26',
    firstName: 'Keaton',
    surname: 'Whitaker',
    region: 'United States',
    age: 28,
    archetype: 'balanced',
    personality: 'streaky',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-27',
    firstName: 'Wei-Lun',
    surname: 'Tsai',
    region: 'Taiwan',
    age: 25,
    archetype: 'precision',
    personality: 'reserved',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-28',
    firstName: 'Bongani',
    surname: 'Dlamini',
    region: 'Eswatini',
    age: 34,
    archetype: 'power-player',
    personality: 'charismatic',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-29',
    firstName: 'Finnian',
    surname: 'Sloane',
    region: 'Ireland',
    age: 24,
    archetype: 'volatile-aggressor',
    personality: 'aggressive',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-30',
    firstName: 'Andres',
    surname: 'Salcedo',
    region: 'Mexico',
    age: 39,
    archetype: 'veteran',
    personality: 'methodical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-31',
    firstName: 'Colton',
    surname: 'Merritt',
    region: 'United States',
    age: 31,
    archetype: 'short-game-specialist',
    personality: 'confident',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-32',
    firstName: 'Yuto',
    surname: 'Kageyama',
    region: 'Japan',
    age: 27,
    archetype: 'course-manager',
    personality: 'analytical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-33',
    firstName: 'Henrik',
    surname: 'Lindqvist',
    region: 'Norway',
    age: 36,
    archetype: 'wind-specialist',
    personality: 'resilient',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-34',
    firstName: 'Declan',
    surname: 'Fenwick',
    region: 'England',
    age: 29,
    archetype: 'elite-putter',
    personality: 'calm',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-35',
    firstName: 'Ari',
    surname: 'Ben-Shahar',
    region: 'Israel',
    age: 32,
    archetype: 'recovery-specialist',
    personality: 'intense',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-36',
    firstName: 'Mitchell',
    surname: 'Bancroft',
    region: 'Australia',
    age: 42,
    archetype: 'veteran',
    personality: 'reserved',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-37',
    firstName: 'Caleb',
    surname: 'Redmond',
    region: 'United States',
    age: 23,
    archetype: 'rising-prospect',
    personality: 'streaky',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-38',
    firstName: 'Tomas',
    surname: 'Hruby',
    region: 'Czechia',
    age: 30,
    archetype: 'balanced',
    personality: 'methodical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-39',
    firstName: 'Kieran',
    surname: 'MacRae',
    region: 'Scotland',
    age: 33,
    archetype: 'major-specialist',
    personality: 'confident',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-40',
    firstName: 'Santiago',
    surname: 'Lujan',
    region: 'Chile',
    age: 26,
    archetype: 'volatile-aggressor',
    personality: 'aggressive',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-41',
    firstName: 'Evan',
    surname: 'Tillinghast',
    region: 'Canada',
    age: 35,
    archetype: 'course-manager',
    personality: 'analytical',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-42',
    firstName: 'Faisal',
    surname: 'Rahmani',
    region: 'United Arab Emirates',
    age: 28,
    archetype: 'precision',
    personality: 'calm',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-43',
    firstName: 'Koa',
    surname: 'Nakoa',
    region: 'United States',
    age: 25,
    archetype: 'power-player',
    personality: 'charismatic',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-44',
    firstName: 'Leon',
    surname: 'Kowalski',
    region: 'Poland',
    age: 38,
    archetype: 'short-game-specialist',
    personality: 'resilient',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-45',
    firstName: 'Bennett',
    surname: 'Gaines',
    region: 'United States',
    age: 29,
    archetype: 'balanced',
    personality: 'confident',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-46',
    firstName: 'Wiremu',
    surname: 'Ropata',
    region: 'New Zealand',
    age: 31,
    archetype: 'wind-specialist',
    personality: 'reserved',
    handedness: 'left',
  },
  {
    id: 'tour-golfer-47',
    firstName: 'Osman',
    surname: 'Ndiaye',
    region: 'Senegal',
    age: 27,
    archetype: 'recovery-specialist',
    personality: 'methodical',
    handedness: 'right',
  },
  {
    id: 'tour-golfer-48',
    firstName: 'Drew',
    surname: 'Hollister',
    region: 'United States',
    age: 34,
    archetype: 'elite-putter',
    personality: 'streaky',
    handedness: 'right',
  },
];

const clamp = (rating: number) => Math.max(55, Math.min(96, rating));

export const TOUR_GOLFERS: readonly TourGolfer[] = ACTIVE_TOUR_IDENTITIES.map((identity, i) => {
  const base = 68 + ((i * 7) % 24);
  const variance = (n: number) => clamp(base + ((i * n) % 13) - 6);
  const boost = (skill: GolferArchetype, amount = 5) => (identity.archetype === skill ? amount : 0);
  return {
    ...identity,
    name: `${identity.firstName} ${identity.surname}`,
    shortName: `${identity.firstName[0]}. ${identity.surname}`,
    overallRating: base,
    drivingDistance: 268 + ((i * 11) % 48) + boost('power-player', 7),
    drivingAccuracy: clamp(variance(3) + boost('precision')),
    approach: clamp(variance(5) + boost('precision') + boost('major-specialist', 3)),
    shortGame: clamp(
      variance(7) + boost('short-game-specialist') + boost('recovery-specialist', 2),
    ),
    putting: clamp(variance(9) + boost('elite-putter')),
    recovery: clamp(variance(11) + boost('recovery-specialist')),
    consistency: clamp(variance(4) + boost('veteran', 3) + boost('balanced', 2)),
    aggression: clamp(55 + ((i * 9) % 41) + boost('volatile-aggressor', 5)),
    courseManagement: clamp(variance(6) + boost('course-manager') + boost('wind-specialist', 2)),
    popularity: 35 + ((i * 17) % 61),
    rookieSeason: 2026 - Math.max(1, identity.age - 20),
    rivalryPotential: 30 + ((i * 19) % 66),
    preferredEnvironments: [['woodland', 'links', 'coastal', 'mountain'][(i * 3) % 4]],
    dislikedEnvironments: [['desert', 'wind', 'soft', 'firm'][(i * 3 + 1) % 4]],
    currentRanking: i + 1,
    seasonPoints: 0,
    careerWins:
      identity.age >= 38
        ? 12 + (i % 13)
        : i < 9
          ? 7 + (i % 10)
          : i < 23
            ? 2 + (i % 7)
            : identity.age <= 24
              ? 0
              : i % 4,
    majorWins:
      identity.age >= 38
        ? 2 + (i % 4)
        : i < 9
          ? i % 4
          : identity.archetype === 'major-specialist'
            ? 1
            : 0,
  };
});
